import { memo, useCallback, useState } from "react";
import {
  Accordion,
  Box,
  Flex,
  Text,
  useMantineTheme,
  Image,
  Skeleton,
  ActionIcon,
  Stack,
} from "@mantine/core";
import { MdEdit } from "react-icons/md";
import TransactionFee from "./TransactionFee";
import MonthlyFee from "./MonthlyFee";
import {
  GetOrganizationRevenueFeeQuery,
  useGetOrganizationRevenueFeeQuery,
} from "src/generated/graphql";
import { useParams } from "react-router-dom";
import { useAppContext } from "src/api/AppContext";
import { openModal } from "@mantine/modals";
import Merchant from "./Merchant";
import PaymentTerminal from "./PaymentTerminal";

export type IRevenueFee = NonNullable<
  GetOrganizationRevenueFeeQuery["organizationOne"]
>["locations"][0]["locationRevenue"];

function RevenueFees() {
  const theme = useMantineTheme();
  const { currentAdmin, currentOrganization } = useAppContext();
  const { orgId = "" } = useParams();
  const [accordianValues, setAccordianValues] = useState<string[]>([]);

  const { data, refetch, loading } = useGetOrganizationRevenueFeeQuery({
    variables: { id: orgId },
    skip: !orgId,
    fetchPolicy: "cache-and-network",
  });
  const { locations } = data?.organizationOne || {};

  const handleModal = useCallback(
    (locationRevenue) => {
      openModal({
        title: "Merchant ID",
        children: (
          <Merchant locationRevenue={locationRevenue} refetch={refetch} />
        ),
      });
    },
    [refetch]
  );

  if (!locations?.length) {
    return (
      <Box mt={"md"}>
        {loading ? (
          <Skeleton height={60} radius="sm" />
        ) : (
          <Text align="center" fw={600}>
            No Revenue and Fee Found
          </Text>
        )}
      </Box>
    );
  }
  return (
    <>
      <Accordion
        multiple
        value={accordianValues}
        onChange={setAccordianValues}
        styles={{
          item: {
            marginTop: 10,
            borderRadius: 8,
            border: "0px",
          },
          control: {
            padding: 8,
            borderRadius: 8,
          },
        }}
      >
        {locations?.map((loc) => {
          const { locationRevenue } = loc;
          const hasFee = !!locationRevenue?.payrocAccount?.merchantId;

          if (!loc.locationRevenue) return <></>;
          return (
            <Accordion.Item
              key={loc.id}
              value={loc.locationRevenue.id}
              sx={{
                background: hasFee ? theme.colors.light[1] : "#FFD8D8",
              }}
            >
              <Accordion.Control
                sx={{
                  ":hover": {
                    background: hasFee ? theme.colors.light[1] : "#FFD8D8",
                  },
                }}
              >
                <Flex align={"center"} justify={"space-between"}>
                  <Flex align={"center"} gap={"sm"}>
                    <Image
                      styles={{
                        placeholder: {
                          background: "#F2F2F2",
                        },
                      }}
                      mt={3}
                      width={50}
                      height={50}
                      maw={50}
                      mah={50}
                      src={loc.logoUrl || ""}
                      alt="location image"
                      withPlaceholder
                      radius={4}
                    />
                    <Text size={20}>{loc.name}</Text>
                  </Flex>

                  {!hasFee && (
                    <Text size={17} fw={500} color="#E30505">
                      Integration Incomplete
                    </Text>
                  )}
                  <Box />
                </Flex>
              </Accordion.Control>
              <Accordion.Panel>
                <Flex>
                  <Flex style={{ flex: 1 }} justify={"space-between"}>
                    <Stack ml={15} spacing={5}>
                      <Text size={16} fw={500}>
                        Merchant ID:
                      </Text>

                      <Text size={16} fw={500}>
                        Virtual Terminal ID:
                      </Text>

                      <Text size={16} fw={500}>
                        Embedded Secret:
                      </Text>
                    </Stack>

                    <Stack spacing={5} justify={"flex-end"}>
                      <Text size={16} fw={500} align="end">
                        {locationRevenue?.payrocAccount?.merchantId}
                      </Text>

                      <Text size={16} fw={500} align="end">
                        {
                          locationRevenue?.payrocAccount
                            ?.payrocVirtualTerminalId
                        }
                      </Text>

                      <Text size={16} fw={500} align="end">
                        {locationRevenue?.payrocAccount?.payrocEmbeddedSecret?.replaceAll(
                          /./g,
                          "*"
                        )}
                      </Text>
                    </Stack>
                  </Flex>
                  <div>
                    {currentAdmin?.id && (
                      <ActionIcon
                        size="md"
                        mr={5}
                        ml={30}
                        onClick={() => handleModal(locationRevenue)}
                        sx={{
                          "&:hover": {
                            backgroundColor: theme.colors.primary[0],
                          },
                        }}
                      >
                        <MdEdit size={18} color={theme.colors.primary[6]} />
                      </ActionIcon>
                    )}
                  </div>
                </Flex>
                <Flex direction={"column"} gap={"xs"} mt={"xs"}>
                  <PaymentTerminal locationId={loc.id} />
                  {accordianValues.includes(locationRevenue!.id) && (
                    <>
                      <TransactionFee
                        locationRevenueId={locationRevenue!.id}
                        locationId={loc.id}
                      />

                      <MonthlyFee
                        locationRevenueId={locationRevenue!.id}
                        locationId={loc.id}
                      />
                    </>
                  )}
                </Flex>
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </>
  );
}

export default memo(RevenueFees);
